import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { X, Loader2, AlertTriangle, Copy, Check } from 'lucide-react';
import API_ENDPOINTS from '../../config/api';

// ─── Styles ──────────────────────────────────────────────────────

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const Modal = styled.div`
  background: #111;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  width: 100%;
  max-width: 1100px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.6);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  flex-shrink: 0;
`;

const HeaderTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  h2 { margin: 0; font-size: 16px; font-weight: 600; color: #fff; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  span { font-size: 12px; color: #666; font-family: monospace; }
`;

const CloseBtn = styled.button`
  width: 32px;
  height: 32px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: #aaa;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.15s;
  &:hover { background: rgba(255, 255, 255, 0.12); color: #fff; }
`;

const TabBar = styled.div`
  display: flex;
  gap: 0;
  padding: 0 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  flex-shrink: 0;
  overflow-x: auto;
`;

const Tab = styled.button`
  padding: 10px 14px;
  background: transparent;
  border: none;
  border-bottom: 2px solid ${p => p.$active ? '#4a9eff' : 'transparent'};
  color: ${p => p.$active ? '#fff' : '#888'};
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
  &:hover { color: #fff; }
`;

const TabBadge = styled.span`
  margin-left: 6px;
  padding: 1px 6px;
  background: rgba(74, 158, 255, 0.15);
  color: #8ab4f8;
  border-radius: 999px;
  font-size: 10px;
  font-family: monospace;
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  min-height: 0;
`;

const State = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 24px;
  color: #888;
  gap: 12px;
  svg.spinning { animation: spin 1s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }
`;

const Code = styled.pre`
  margin: 0;
  padding: 14px 16px;
  background: #0a0a0a;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  color: #ddd;
  font-family: 'SF Mono', Menlo, monospace;
  font-size: 12px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-x: auto;
  position: relative;
`;

const CopyBtn = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  padding: 4px 8px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  color: #aaa;
  font-size: 11px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  &:hover { background: rgba(255, 255, 255, 0.12); color: #fff; }
`;

const Section = styled.div`
  margin-bottom: 20px;
  &:last-child { margin-bottom: 0; }
`;

const SectionTitle = styled.h3`
  margin: 0 0 8px;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #888;
  font-weight: 600;
`;

const KvGrid = styled.div`
  display: grid;
  grid-template-columns: 160px 1fr;
  gap: 8px 16px;
  font-size: 13px;
`;

const KvLabel = styled.div`
  color: #666;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const KvValue = styled.div`
  color: #ddd;
  font-family: ${p => p.$mono ? 'monospace' : 'inherit'};
  word-break: break-all;
`;

// ─── Helpers ─────────────────────────────────────────────────────

function CopyableCode({ value }) {
  const [copied, setCopied] = useState(false);
  const text = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  return (
    <Code>
      <CopyBtn
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          } catch { /* clipboard unavailable */ }
        }}
      >
        {copied ? <><Check size={11} /> Kopioitu</> : <><Copy size={11} /> Kopioi</>}
      </CopyBtn>
      {text}
    </Code>
  );
}

function fmt(v) {
  if (v == null) return '—';
  if (typeof v === 'number') return v.toLocaleString('fi-FI');
  return String(v);
}

// ─── Tab content ─────────────────────────────────────────────────

function OverviewTab({ entry, source }) {
  if (source === 'ai_request') {
    return (
      <Section>
        <SectionTitle>AI-pyyntö</SectionTitle>
        <KvGrid>
          <KvLabel>Aika</KvLabel><KvValue $mono>{new Date(entry.timestamp).toLocaleString('fi-FI')}</KvValue>
          <KvLabel>Prompt-avain</KvLabel><KvValue $mono>{entry.prompt_key} (v{fmt(entry.prompt_version)})</KvValue>
          <KvLabel>Endpoint</KvLabel><KvValue $mono>{entry.endpoint || '—'}</KvValue>
          <KvLabel>AI-malli</KvLabel><KvValue $mono>{entry.ai_model}</KvValue>
          <KvLabel>Lämpötila</KvLabel><KvValue $mono>{fmt(entry.temperature)}</KvValue>
          <KvLabel>Max tokens</KvLabel><KvValue $mono>{fmt(entry.max_tokens)}</KvValue>
          <KvLabel>Vaihe</KvLabel><KvValue $mono>{entry.session_phase || '—'}</KvValue>
          <KvLabel>Tokenit (in/out)</KvLabel><KvValue $mono>{fmt(entry.tokens_input)} / {fmt(entry.tokens_output)}</KvValue>
          <KvLabel>Cache (read/create)</KvLabel><KvValue $mono>{fmt(entry.tokens_cache_read)} / {fmt(entry.tokens_cache_creation)}</KvValue>
          <KvLabel>Hinta</KvLabel><KvValue $mono>${entry.estimated_cost_usd?.toFixed(4) ?? '—'}</KvValue>
          <KvLabel>Kesto (kok / RAG / AI / polish)</KvLabel><KvValue $mono>{fmt(entry.duration_ms)} / {fmt(entry.rag_duration_ms)} / {fmt(entry.ai_duration_ms)} / {fmt(entry.polish_duration_ms)} ms</KvValue>
          <KvLabel>RAG-osumat</KvLabel><KvValue $mono>{fmt(entry.rag_results_count)}</KvValue>
          <KvLabel>Stop reason</KvLabel><KvValue $mono>{entry.stop_reason || '—'}</KvValue>
          <KvLabel>Onnistui</KvLabel><KvValue $mono>{entry.success ? '✓' : '✗'}</KvValue>
          <KvLabel>Parent ID</KvLabel><KvValue $mono>{entry.parent_request_id || '—'}</KvValue>
          <KvLabel>Instanssi</KvLabel><KvValue $mono>{entry.instance_id || '—'}</KvValue>
          <KvLabel>Laite</KvLabel><KvValue $mono>{entry.device_id || '—'}</KvValue>
        </KvGrid>
        {entry.error_message && (
          <Section style={{ marginTop: 16 }}>
            <SectionTitle>Virhe</SectionTitle>
            <Code style={{ color: '#ef4444' }}>{entry.error_message}</Code>
          </Section>
        )}
      </Section>
    );
  }

  return (
    <Section>
      <SectionTitle>Toiminta</SectionTitle>
      <KvGrid>
        <KvLabel>Aika</KvLabel><KvValue $mono>{new Date(entry.timestamp).toLocaleString('fi-FI')}</KvValue>
        <KvLabel>Metodi</KvLabel><KvValue $mono>{entry.method}</KvValue>
        <KvLabel>Polku</KvLabel><KvValue $mono>{entry.path}</KvValue>
        <KvLabel>Tila</KvLabel><KvValue $mono>{entry.status_code}</KvValue>
        <KvLabel>Kesto</KvLabel><KvValue $mono>{fmt(entry.duration_ms)} ms</KvValue>
        <KvLabel>Yhteenveto</KvLabel><KvValue>{entry.summary || '—'}</KvValue>
        <KvLabel>Toimija</KvLabel><KvValue $mono>{entry.actor || '—'}</KvValue>
        <KvLabel>Laite</KvLabel><KvValue $mono>{entry.device_id || '—'}</KvValue>
        <KvLabel>Instanssi</KvLabel><KvValue $mono>{entry.instance_id || '—'}</KvValue>
      </KvGrid>
    </Section>
  );
}

// ─── Modal ───────────────────────────────────────────────────────

/**
 * LogDetailModal — fetches and displays the full payload for one log row.
 *
 * Props:
 *   entry: { id, source: 'action_log' | 'ai_request', summary, ... } from listing
 *   onClose: () => void
 */
export default function LogDetailModal({ entry, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');

  useEffect(() => {
    let cancelled = false;
    const url = `${API_ENDPOINTS.ACTION_LOG_DETAIL()}?source=${entry.source}&id=${entry.id}`;
    setLoading(true);
    setError(null);
    fetch(url, { credentials: 'include' })
      .then(r => r.ok ? r.json() : r.json().then(j => Promise.reject(new Error(j.error || `HTTP ${r.status}`))))
      .then(d => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch(err => { if (!cancelled) { setError(err.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [entry.id, entry.source]);

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const isAi = entry.source === 'ai_request';

  // Tab visibility: only show tabs that have content
  const tabs = [{ id: 'overview', label: 'Yhteenveto' }];
  if (data) {
    if (isAi) {
      if (data.system_prompt || data.user_prompt) tabs.push({ id: 'prompt', label: 'Prompt' });
      if (data.rag_query || data.rag_results) tabs.push({ id: 'rag', label: 'RAG', badge: data.rag_results_count });
      if (data.response_text || data.response_parsed) tabs.push({ id: 'response', label: 'Vastaus' });
    } else {
      if (data.request_body) tabs.push({ id: 'body', label: 'Pyyntö' });
    }
    tabs.push({ id: 'raw', label: 'Raw' });
  }

  return (
    <Backdrop onClick={onClose}>
      <Modal onClick={e => e.stopPropagation()}>
        <Header>
          <HeaderTitle>
            <h2>{entry.summary || (isAi ? 'AI-pyyntö' : 'Toiminta')}</h2>
            <span>{entry.source} · #{entry.id} · {new Date(entry.timestamp).toLocaleString('fi-FI')}</span>
          </HeaderTitle>
          <CloseBtn onClick={onClose}><X size={18} /></CloseBtn>
        </Header>

        {loading && (
          <State><Loader2 size={28} className="spinning" />Ladataan tietoja…</State>
        )}

        {error && (
          <State>
            <AlertTriangle size={28} color="#ef4444" />
            <div>Lataus epäonnistui: {error}</div>
          </State>
        )}

        {data && !loading && (
          <>
            <TabBar>
              {tabs.map(t => (
                <Tab key={t.id} $active={tab === t.id} onClick={() => setTab(t.id)}>
                  {t.label}
                  {t.badge != null && <TabBadge>{t.badge}</TabBadge>}
                </Tab>
              ))}
            </TabBar>
            <Body>
              {tab === 'overview' && <OverviewTab entry={data} source={entry.source} />}

              {tab === 'prompt' && (
                <>
                  {data.system_prompt && (
                    <Section>
                      <SectionTitle>System prompt ({data.system_prompt.length} merkkiä)</SectionTitle>
                      <CopyableCode value={data.system_prompt} />
                    </Section>
                  )}
                  {data.user_prompt && (
                    <Section>
                      <SectionTitle>User prompt ({data.user_prompt.length} merkkiä)</SectionTitle>
                      <CopyableCode value={data.user_prompt} />
                    </Section>
                  )}
                  {data.prompt_variables && Object.keys(data.prompt_variables).length > 0 && (
                    <Section>
                      <SectionTitle>Prompt-muuttujat</SectionTitle>
                      <CopyableCode value={data.prompt_variables} />
                    </Section>
                  )}
                </>
              )}

              {tab === 'rag' && (
                <>
                  {data.rag_query && (
                    <Section>
                      <SectionTitle>RAG-haku</SectionTitle>
                      <CopyableCode value={data.rag_query} />
                    </Section>
                  )}
                  {data.rag_results && (
                    <Section>
                      <SectionTitle>RAG-osumat ({data.rag_results_count})</SectionTitle>
                      <CopyableCode value={data.rag_results} />
                    </Section>
                  )}
                  {data.rag_citations_used && (
                    <Section>
                      <SectionTitle>Käytetyt sitaatit</SectionTitle>
                      <CopyableCode value={data.rag_citations_used} />
                    </Section>
                  )}
                </>
              )}

              {tab === 'response' && (
                <>
                  {data.response_parsed && (
                    <Section>
                      <SectionTitle>Vastaus (parsittu JSON)</SectionTitle>
                      <CopyableCode value={data.response_parsed} />
                    </Section>
                  )}
                  {data.response_final && (
                    <Section>
                      <SectionTitle>Lopullinen vastaus</SectionTitle>
                      <CopyableCode value={data.response_final} />
                    </Section>
                  )}
                  {data.response_text && (
                    <Section>
                      <SectionTitle>Raaka vastaus</SectionTitle>
                      <CopyableCode value={data.response_text} />
                    </Section>
                  )}
                </>
              )}

              {tab === 'body' && data.request_body && (
                <Section>
                  <SectionTitle>Pyynnön sisältö (sanitoituna, max 1KB)</SectionTitle>
                  <CopyableCode value={data.request_body} />
                </Section>
              )}

              {tab === 'raw' && (
                <Section>
                  <SectionTitle>Raw row</SectionTitle>
                  <CopyableCode value={data} />
                </Section>
              )}
            </Body>
          </>
        )}
      </Modal>
    </Backdrop>
  );
}
